import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  GetApp as GetAppIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  FactCheck as FactCheckIcon,
  Code as CodeIcon,
  Send as SendIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Footer from '../components/Footer';
import { useAuth } from '../App';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * API maison de repos : les lignes agrégées sont dans facturesCharmilles (par résident), pas dans facture.lignes.
 */
function lignesDetailPourAffichage(facture) {
  if (!facture) return { rows: [], showResident: false };
  if (Array.isArray(facture.facturesCharmilles) && facture.facturesCharmilles.length > 0) {
    const rows = [];
    facture.facturesCharmilles.forEach((seg) => {
      const resident = [seg.client_prenom, seg.client_nom].filter(Boolean).join(' ').trim() || '—';
      (seg.lignes || []).forEach((ligne) => {
        rows.push({ ligne, resident });
      });
    });
    return { rows, showResident: true };
  }
  const lignes = facture.lignes || [];
  return {
    rows: lignes.map((ligne) => ({ ligne, resident: null })),
    showResident: false,
  };
}

function DetailFacture() {
  const { user } = useAuth();
  const [facture, setFacture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [peppolState, setPeppolState] = useState({ loading: false, result: null });
  const [maisonNoms, setMaisonNoms] = useState([]);
  const [editingMaison, setEditingMaison] = useState(false);
  const [maisonDraft, setMaisonDraft] = useState('');
  const [savingMaison, setSavingMaison] = useState(false);
  // Variable menuAnchor supprimée (menu de statut n'existe plus)
  const { id } = useParams();
  const navigate = useNavigate();

  // Constante statuts supprimée (colonne statut n'existe plus)

  const fetchFacture = useCallback(async () => {
    try {
      const response = await axios.get(`/api/factures/${id}`);
      setFacture(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de la facture:', error);
      showSnackbar('Erreur lors du chargement de la facture', 'error');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFacture();
  }, [fetchFacture]);

  const { rows: lignesAffichage, showResident: afficherColonneResident } = useMemo(
    () => lignesDetailPourAffichage(facture),
    [facture]
  );

  const maisonOptionsListe = useMemo(() => {
    const s = new Set(maisonNoms);
    const cur = facture?.maison_repos;
    if (cur && String(cur).trim() !== '' && !s.has(cur)) {
      s.add(cur);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [maisonNoms, facture?.maison_repos]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/maisons-repos', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const noms = (Array.isArray(data) ? data : [])
            .map((m) => m.nom)
            // Exclusion volontaire de "Notre foyer" des menus déroulants de sélection
            .filter((nom) => String(nom || '').trim().toLowerCase() !== 'notre foyer');
          setMaisonNoms(noms);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`/api/factures/${id}/pdf`, {
        responseType: 'blob',
      });
      
      // Extraire le nom de fichier depuis les headers de la réponse
      const contentDisposition = response.headers['content-disposition'];
      let filename = `facture-${facture?.numero || id}.pdf`; // Nom par défaut
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      showSnackbar('Erreur lors du téléchargement du PDF', 'error');
    }
  };

  // Fonctions de gestion de la suppression
  const handleDeleteDialogOpen = () => {
    setDeleteDialog(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialog(false);
  };

  const handleDeleteFacture = async () => {
    try {
      await axios.delete(`/api/factures/${id}`);
      showSnackbar('Facture supprimée avec succès', 'success');
      navigate('/factures'); // Retourner à la liste des factures
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la suppression de la facture';
      showSnackbar(errorMessage, 'error');
    }
    setDeleteDialog(false);
  };

  // Vérifier si l'utilisateur peut supprimer des factures
  const canDeleteFactures = user?.role === 'admin' || user?.role === 'superadmin';
  const canSendPeppol = user?.role === 'admin' || user?.role === 'superadmin';

  const isGroupedPeppol = facture && maisonNoms.includes(facture.maison_repos);

  const runPeppolValidate = async () => {
    setPeppolState({ loading: true, result: null });
    try {
      const { data } = await axios.get(`/api/factures/${id}/peppol/validate`);
      setPeppolState({ loading: false, result: data });
      if (data.valid) {
        showSnackbar('Conformité PEPPOL : OK (contrôles applicatifs)', 'success');
      } else if (data.mode === 'grouped') {
        showSnackbar('Complétez les données PEPPOL de l’établissement (onglet Maisons de repos) — voir le détail', 'warning');
      } else {
        showSnackbar('Des champs PEPPOL manquent — voir le détail ci-dessous', 'warning');
      }
    } catch (error) {
      setPeppolState({ loading: false, result: null });
      showSnackbar(error.response?.data?.error || 'Erreur validation PEPPOL', 'error');
    }
  };

  const handleDownloadUbl = async () => {
    try {
      const response = await axios.get(`/api/factures/${id}/peppol/ubl.xml`, {
        responseType: 'blob',
        validateStatus: () => true,
      });
      if (response.status !== 200) {
        let msg = 'Erreur génération UBL';
        try {
          const text = await response.data.text();
          const j = JSON.parse(text);
          if (j.errors?.length) {
            msg = j.errors.map((e) => e.message).join(' ; ') || j.error || msg;
          } else if (j.error) msg = j.error;
        } catch {
          /* ignore */
        }
        showSnackbar(msg, 'error');
        return;
      }
      const filename = `facture-${facture?.numero || id}.xml`.replace(/[^a-zA-Z0-9._-]/g, '_');
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar('XML UBL téléchargé', 'success');
    } catch (error) {
      console.error(error);
      showSnackbar('Erreur téléchargement UBL', 'error');
    }
  };

  const handleSendPeppol = async () => {
    try {
      const { data } = await axios.post(`/api/factures/${id}/peppol/send`, {});
      showSnackbar(data.message || 'Envoyé à l’access point', 'success');
    } catch (error) {
      const d = error.response?.data;
      const msg =
        d?.error ||
        (d?.errors && d.errors.map((e) => e.message).join(' ; ')) ||
        'Erreur envoi access point';
      showSnackbar(msg, 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStartEditMaison = () => {
    setMaisonDraft(facture.maison_repos || '');
    setEditingMaison(true);
  };

  const handleCancelEditMaison = () => {
    setEditingMaison(false);
    setMaisonDraft('');
  };

  const handleSaveMaison = async () => {
    setSavingMaison(true);
    try {
      const { data } = await axios.put(`/api/factures/${id}`, {
        maison_repos: maisonDraft === '' ? null : maisonDraft,
      });
      setFacture((prev) => (prev ? { ...prev, maison_repos: data.maison_repos } : prev));
      setEditingMaison(false);
      showSnackbar('Maison de repos enregistrée sur la facture', 'success');
      await fetchFacture();
    } catch (error) {
      console.error(error);
      showSnackbar(error.response?.data?.error || 'Enregistrement impossible', 'error');
    } finally {
      setSavingMaison(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  if (!facture) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Facture non trouvée
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/factures')}
          sx={{ mt: 2 }}
        >
          Retour aux factures
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 8 }}>
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/factures')}
            sx={{ mr: 2 }}
          >
            Retour
          </Button>
          <Typography variant="h4">
            Facture {facture.numero}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleDownloadPDF}
          >
            Télécharger PDF
          </Button>
          {canDeleteFactures && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteDialogOpen}
              sx={{ ml: 1 }}
            >
              Supprimer
            </Button>
          )}
          {/* Chip de statut et bouton de modification supprimés (colonne statut n'existe plus) */}
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        {/* Informations générales */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informations générales
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Numéro de facture
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                {facture.numero}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Date de facture
              </Typography>
              <Typography variant="body1">
                {format(new Date(facture.date_facture + 'T00:00:00'), 'dd/MM/yyyy', { locale: fr })}
              </Typography>
            </Box>
            {facture.date_echeance && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Date d'échéance
                </Typography>
                <Typography variant="body1">
                  {format(new Date(facture.date_echeance + 'T00:00:00'), 'dd/MM/yyyy', { locale: fr })}
                </Typography>
              </Box>
            )}
            {facture.notes && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Notes
                </Typography>
                <Typography variant="body1">
                  {facture.notes}
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Maison de repos
              </Typography>
              {!editingMaison ? (
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body1">
                    {facture.maison_repos || '— (non renseignée sur la facture)'}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleStartEditMaison}
                  >
                    Modifier
                  </Button>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={1} alignItems="flex-start">
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id="maison-facture-label">Établissement</InputLabel>
                    <Select
                      labelId="maison-facture-label"
                      label="Établissement"
                      value={maisonDraft}
                      onChange={(e) => setMaisonDraft(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Non renseignée</em>
                      </MenuItem>
                      {maisonOptionsListe.map((nom) => (
                        <MenuItem key={nom} value={nom}>
                          {nom}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={savingMaison}
                      onClick={handleSaveMaison}
                    >
                      {savingMaison ? 'Enregistrement…' : 'Enregistrer'}
                    </Button>
                    <Button size="small" disabled={savingMaison} onClick={handleCancelEditMaison}>
                      Annuler
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Informations client */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Client
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {facture.client_prenom ? `${facture.client_prenom} ${facture.client_nom}` : facture.client_nom}
              </Typography>
            </Box>
            {facture.adresse && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {facture.adresse}
                </Typography>
              </Box>
            )}
            {(facture.code_postal || facture.ville) && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {facture.code_postal} {facture.ville}
                </Typography>
              </Box>
            )}
            {facture.telephone && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Tél: {facture.telephone}
                </Typography>
              </Box>
            )}
            {facture.client_email && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2">
                  Email: {facture.client_email}
                </Typography>
              </Box>
            )}
            {facture.numero_tva && (
              <Box>
                <Typography variant="body2">
                  N° TVA: {facture.numero_tva}
                </Typography>
              </Box>
            )}
            {facture.peppol_endpoint_scheme && facture.peppol_endpoint_id && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  PEPPOL
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {facture.peppol_endpoint_scheme}:{facture.peppol_endpoint_id}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Détail des lignes */}
        <Grid item xs={12}>
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white' }}>
              <Typography variant="h6">Détail de la facture</Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {afficherColonneResident && <TableCell>Résident</TableCell>}
                    <TableCell>Article</TableCell>
                    <TableCell>Référence</TableCell>
                    <TableCell align="right">Quantité</TableCell>
                    <TableCell align="right">Total TTC</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lignesAffichage.map(({ ligne, resident }, index) => {
                    const ligne_ht = ligne.quantite * ligne.prix_unitaire;
                    const ligne_tva = ligne_ht * (ligne.tva / 100);
                    const ligne_ttc = ligne_ht + ligne_tva;
                    const key = ligne.id != null ? `l-${ligne.id}` : `r-${resident}-${index}`;

                    return (
                      <TableRow key={key}>
                        {afficherColonneResident && (
                          <TableCell>
                            <Typography variant="body2">{resident || '—'}</Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {ligne.article_nom}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                            {ligne.article_reference}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{ligne.quantite}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {ligne_ttc.toFixed(2)} €
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Totaux */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" gutterBottom>
                    Récapitulatif
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      Total TTC: {facture.total_ttc.toFixed(2)} €
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* E-facturation PEPPOL */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              E-facturation PEPPOL (UBL)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isGroupedPeppol
                ? `Pour ${facture.maison_repos}, le destinataire PEPPOL est la maison de repos (onglet Maisons de repos), pas les résidents. Un seul UBL regroupe toutes les lignes ; chaque ligne indique le résident concerné.`
                : 'Vérifiez les champs obligatoires, téléchargez le XML UBL, puis envoyez via votre access point (configuration serveur .env — voir README_PEPPOL.md).'}
            </Typography>
            {peppolState.result?.peppolGroupedNote && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {peppolState.result.peppolGroupedNote}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FactCheckIcon />}
                onClick={runPeppolValidate}
                disabled={peppolState.loading}
              >
                Vérifier les champs
              </Button>
              <Button variant="outlined" startIcon={<CodeIcon />} onClick={handleDownloadUbl}>
                Télécharger XML UBL
              </Button>
              {canSendPeppol && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SendIcon />}
                  onClick={handleSendPeppol}
                >
                  Envoyer à l’access point
                </Button>
              )}
            </Box>
            {peppolState.result && !peppolState.result.valid && peppolState.result.errors?.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {isGroupedPeppol
                    ? 'Fournisseur et/ou établissement (Maisons de repos) — à compléter'
                    : 'Fournisseur, client ou facture — à compléter'}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {peppolState.result.errors.map((e, i) => (
                    <li key={i}>
                      <Typography variant="body2">{e.message}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            {peppolState.result?.valid && (
              <Alert severity="success" sx={{ mt: 1 }}>
                Contrôles applicatifs OK — validez tout de même le XML avec votre access point avant production.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialog} onClose={handleDeleteDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          {facture && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Cette action est irréversible !
              </Alert>
              <Typography variant="body1" gutterBottom>
                Êtes-vous sûr de vouloir supprimer cette facture ?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Facture : {facture.numero}
                </Typography>
                <Typography variant="body2">
                  Client : {facture.client_prenom ? 
                    `${facture.client_prenom} ${facture.client_nom}` : 
                    facture.client_nom}
                </Typography>
                <Typography variant="body2">
                  Montant : {facture.total_ttc?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </Typography>
                <Typography variant="body2">
                  Date : {new Date(facture.date_facture).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteFacture} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      {/* Footer avec mention Woud */}
      <Footer />
    </Box>
  );
}

export default DetailFacture; 